import { useEffect, useRef, useState } from "react";
import { QRCodeCanvas } from "qrcode.react";
import { DownloadSimple, Trash, Plus, Key } from "@phosphor-icons/react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { api, errMsg, guestUrl, khUrl } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { CopyButton } from "@/components/common";
import { PrintKit } from "./PrintKit";

function downloadCanvas(wrap, name) {
  const c = wrap?.querySelector("canvas");
  if (!c) return;
  const a = document.createElement("a");
  a.href = c.toDataURL("image/png");
  a.download = name;
  a.click();
}

function KhRow({ kh, onDelete }) {
  const ref = useRef();
  const url = khUrl(kh.code);
  return (
    <div className="flex flex-wrap items-center gap-3 rounded-xl border border-border p-3" data-testid={`keyholder-row-${kh.kh_id}`}>
      <div ref={ref} className="shrink-0"><QRCodeCanvas value={url} size={56} marginSize={1} /></div>
      <div className="min-w-0 flex-1"><p className="font-semibold">{kh.label}</p><p className="truncate font-mono text-xs text-muted-foreground">{url}</p></div>
      <CopyButton value={url} testId={`copy-kh-${kh.kh_id}`} className="text-sm font-semibold text-[#D96C4A]" done="Link copied" />
      <button onClick={() => downloadCanvas(ref.current, `keyholder-${kh.label}.png`)} data-testid={`download-kh-qr-${kh.kh_id}`} className="text-muted-foreground hover:text-foreground"><DownloadSimple size={18} /></button>
      <button onClick={onDelete} data-testid={`delete-kh-${kh.kh_id}`} className="text-muted-foreground hover:text-destructive"><Trash size={18} /></button>
    </div>
  );
}

export function ShareKit({ property }) {
  const { user } = useAuth();
  const qrRef = useRef();
  const [khs, setKhs] = useState([]);
  const [label, setLabel] = useState("");
  const url = guestUrl(property.slug);
  const limit = user?.subscription?.keyholder_limit;
  const isOwner = user?.role === "owner";

  useEffect(() => { api.get(`/properties/${property.property_id}/keyholders`).then((r) => setKhs(r.data)); }, [property.property_id]);

  const add = async () => {
    if (!label.trim()) return toast.error("Give the key holder a label, e.g. Front door");
    try {
      const r = await api.post(`/properties/${property.property_id}/keyholders`, { label });
      setKhs((k) => [...k, r.data]);
      setLabel("");
    } catch (e) { toast.error(errMsg(e)); }
  };
  const del = async (id) => {
    try { await api.delete(`/keyholders/${id}`); setKhs((k) => k.filter((x) => x.kh_id !== id)); } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div className="space-y-5" data-testid="share-kit">
      <section className="grid gap-6 rounded-2xl border border-border bg-white p-6 sm:grid-cols-[auto,1fr]">
        <div ref={qrRef} className="rounded-xl border border-border p-3" data-testid="share-qr"><QRCodeCanvas value={url} size={168} marginSize={2} /></div>
        <div className="min-w-0">
          <h3 className="font-display text-lg">Guest link & QR code</h3>
          <p className="mt-1 text-sm text-muted-foreground">This link never changes, even after you edit the page. Put the QR on the wall or fridge.</p>
          <div className="mt-4 flex items-center gap-2 rounded-lg bg-secondary px-3 py-2.5"><span className="truncate font-mono text-sm" data-testid="share-guest-url">{url}</span></div>
          <div className="mt-3 flex flex-wrap gap-2">
            <CopyButton value={url} testId="share-copy-link-button" label="Copy link" done="Link copied" className="h-10 rounded-lg bg-[#151312] px-4 text-sm font-semibold text-white" />
            <button onClick={() => downloadCanvas(qrRef.current, `${property.data.name}-qr.png`)} data-testid="share-download-qr-button" className="inline-flex h-10 items-center gap-2 rounded-lg border border-border px-4 text-sm font-semibold"><DownloadSimple /> Download QR</button>
            <a href={url} target="_blank" rel="noreferrer" data-testid="share-open-page-button" className="inline-flex h-10 items-center rounded-lg border border-border px-4 text-sm font-semibold">Open page</a>
          </div>
        </div>
      </section>
      <section className="rounded-2xl border border-border bg-white p-6">
        <div className="flex items-center justify-between">
          <h3 className="flex items-center gap-2 font-display text-lg"><Key /> Key holders</h3>
          {!isOwner && <span className="text-sm text-muted-foreground" data-testid="keyholder-count">{khs.length} / {limit}</span>}
        </div>
        <p className="mt-1 text-sm text-muted-foreground">Each key holder gets its own link, so the PDF shows which one the guest tapped.</p>
        <div className="mt-4 flex gap-2">
          <Input placeholder="Label, e.g. Front door key" value={label} onChange={(e) => setLabel(e.target.value)} onKeyDown={(e) => e.key === "Enter" && add()} data-testid="keyholder-label-input" />
          <button onClick={add} data-testid="keyholder-add-button" className="inline-flex shrink-0 items-center gap-1.5 rounded-lg bg-[#D96C4A] px-4 text-sm font-semibold text-white"><Plus weight="bold" /> Add</button>
        </div>
        <div className="mt-4 space-y-2">{khs.map((k) => <KhRow key={k.kh_id} kh={k} onDelete={() => del(k.kh_id)} />)}</div>
      </section>
      <PrintKit property={property} keyholders={khs} />
    </div>
  );
}
